import React, { useCallback, useEffect, useRef, useState } from "react";
import * as S from "./DesktopHeroSimple.styles";
import { IHeroProps } from "./DesktopHeroSimple.types";
import { absoluteAssetUrl, getOptimizedImageUrl } from "util/assets";
import { ImageSkeletonLoader } from "components/ImageSkeletonLoader";
import { useVideoCleanup } from "hooks/useDestroyVideoRef";

export const DesktopHeroSimple = ({
  config,
  isInView,
}: Readonly<IHeroProps>) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const activeVideoRef = useRef<HTMLVideoElement>(null);
  useVideoCleanup(videoRef);
  useVideoCleanup(activeVideoRef);

  const [isLoopActive, setIsLoopActive] = useState(false);
  const [hasLoadedMainAsset, setHasLoadedMainAsset] = useState(false);

  const onVideoPlaybackEnd = useCallback(async () => {
    if (config.loopSrc && activeVideoRef?.current) {
      setIsLoopActive(true);
    } else {
      try {
        videoRef.current.currentTime = 0;
        await videoRef.current.play();
      } catch (error) {
        console.error("Error playing main video:", error);
      }
    }
  }, [config.loopSrc]);

  const onLoadedMainVideo = useCallback(() => {
    setHasLoadedMainAsset(true);
  }, []);

  useEffect(() => {
    const intro = videoRef.current;
    const activeVideo = activeVideoRef.current;

    if (!isInView) {
      try {
        intro?.pause();
        activeVideo?.pause();
      } catch (error) {
        console.error("Error pausing video:", error);
      }
    } else {
      try {
        if (isLoopActive) {
          activeVideo?.play();
        } else {
          intro?.play();
        }

        if (activeVideo) {
          activeVideo.currentTime = 0;
        }
      } catch (error) {
        console.error("Error playing video:", error);
      }
    }
  }, [isInView, isLoopActive]);

  return (
    <S.HeroContainer>
      <S.Wrapper>
        {!!config?.showGradient && <S.Gradient />}

        <S.ActiveVideoWrapper>
          <ImageSkeletonLoader isVisible={!hasLoadedMainAsset} />

          {!!config?.loopSrc && (
            <S.ActiveVideo
              ref={activeVideoRef}
              src={config.loopSrc}
              muted
              preload="auto"
              loop
              $isActive={isLoopActive}
              playsInline
            />
          )}

          <S.BackgroundVideo
            ref={videoRef}
            src={config?.src}
            muted
            playsInline
            autoPlay
            poster={getOptimizedImageUrl(
              absoluteAssetUrl(config?.poster),
              1920 * 2,
            )}
            $isVisible={!isLoopActive}
            preload="none"
            onLoadedData={onLoadedMainVideo}
            onEnded={onVideoPlaybackEnd}
          />
        </S.ActiveVideoWrapper>
      </S.Wrapper>
    </S.HeroContainer>
  );
};
